import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { channelGroups, channels, type ChannelGroup } from "@/mocks/channels";
import { articles } from "@/mocks/articles";
import { toneClasses } from "@/lib/ui";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ChannelDirectory from "./components/ChannelDirectory";

type FilterKey = ChannelGroup | "全部";
const filters: FilterKey[] = ["全部", ...channelGroups];

const SITE = "https://zozxnbl.cn";

export default function Lanmu() {
  const [active, setActive] = useState<FilterKey>("全部");
  const visibleChannels = active === "全部" ? channels : channels.filter((c) => c.group === active);

  useSeo({
    title: "栏目导航 | 51爆料网内容板块入口与站点导览",
    description:
      "51爆料网栏目导航页汇总首页栏目、专题频道与内容板块入口，按分组呈现每个栏目的位置与内容方向，可直接跳转到对应频道页，是查找爆料分类的最快路径。",
    keywords: "51爆料网栏目导航,内容板块入口,站点导览,栏目分类,频道入口",
    canonical: "/lanmu",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "51爆料网栏目导航",
        url: `${SITE}/lanmu`,
        inLanguage: "zh-CN",
        description:
          "汇总首页栏目、专题频道与内容板块入口的站点导览页，方便快速定位各类内容板块。",
        hasPart: channels.map((c) => ({
          "@type": "WebPage",
          name: c.name,
          url: `${SITE}/pindao/${c.slug}`,
          description: c.description,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE },
          { "@type": "ListItem", position: 2, name: "栏目导航", item: `${SITE}/lanmu` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "51爆料网全部栏目入口",
        itemListElement: channels.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          url: `${SITE}/pindao/${c.slug}`,
        })),
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "栏目导航" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                51爆料网栏目导航
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">
                这里是本站的站点导览入口，汇总首页栏目、专题频道与内容板块入口，方便快速找到爆料分类。
                如果你想直接看深度调查、实时热点或具体事件页，先在下方定位对应板块，再进入频道页浏览，会比逐页翻找更省时间。
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-sm font-bold text-foreground-950">导览速览</p>
                <dl className="mt-3 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-600">栏目总数</dt>
                    <dd className="font-medium text-foreground-950">{channels.length} 个</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-600">入口分组</dt>
                    <dd className="font-medium text-foreground-950">{channelGroups.length} 类</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-600">内容条目</dt>
                    <dd className="font-medium text-foreground-950">{articles.length} 篇示例</dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {channelGroups.map((g) => (
                    <span key={g} className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950">按分组浏览栏目入口</h2>
          <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {filters.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleChannels.map((channel) => {
            const tone = toneClasses[channel.tone];
            const count = articles.filter((a) => a.channel === channel.slug).length;
            return (
              <div
                key={channel.slug}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200"
              >
                <div className="flex items-start justify-between">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-md ${tone.bgSoft} ${tone.textStrong}`}>
                    <i className={`${channel.icon} text-xl`} />
                  </span>
                  <span className="rounded bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
                    {channel.group}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{channel.name}</h3>
                <p className={`mt-1 text-xs font-medium ${tone.textSoft}`}>{channel.tagline}</p>
                <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
                  {channel.description}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                  <span className="text-xs text-foreground-500">
                    {count} 篇 · {channel.position}
                  </span>
                  <Link
                    to={`/pindao/${channel.slug}`}
                    className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
                  >
                    进入板块
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-6 py-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950">
            全部栏目与内容板块入口索引
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            下表按分组列出每个栏目的页面位置与内容方向，方便你判断应该从哪个板块进入。
          </p>
          <div className="mt-6">
            <ChannelDirectory />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 py-12">
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 md:p-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-accent-950">
                已经知道想看哪个板块？
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-accent-800">
                直接进入深度调查频道，或回到首页查看当期编辑推荐；两个入口都能快速开始阅读。
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/pindao/shen-du-diao-cha"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
              >
                <i className="ri-search-eye-line" />
                深度调查频道
              </Link>
              <Link
                to="/"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-300 px-5 py-2.5 text-sm font-medium text-accent-900 transition-colors hover:bg-background-50"
              >
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}